import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FormGroup, FormText, Input, Label } from 'reactstrap';
import { z } from 'zod';
import { useTenantContext } from '../../auth/useTenantContext';
import { useUpdateAdminWarehouse } from '../../api/adminWarehouses';

export interface WarehouseDailyMailSettingsProps {
  warehouseId: number;
  initial: { enabled: boolean; time: string; recipients: string[]; lastClosedOn: string | null };
  canEdit: boolean;
}

const MAX_RECIPIENTS = 20;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const emailSchema = z.string().email();

/** Elenco da textarea: righe, virgole e punto e virgola; trim, senza vuoti. */
function parseRecipients(raw: string): string[] {
  return raw.split(/[\n,;]+/).map((s) => s.trim()).filter((s) => s !== '');
}

/** 'YYYY-MM-DD' -> 'dd/mm/yyyy' (nessun fuso: e' un giorno di calendario). */
function formatDay(d: string): string {
  const [y, m, g] = d.split('-');
  return `${g}/${m}/${y}`;
}

/** Mail giornaliera dei parziali: form autonomo con proprio Salva. */
export function WarehouseDailyMailSettings({ warehouseId, initial, canEdit }: WarehouseDailyMailSettingsProps) {
  const { t } = useTranslation('warehouse');
  const { token, tenantCode } = useTenantContext();
  const update = useUpdateAdminWarehouse(token, tenantCode);

  const [enabled, setEnabled] = useState(initial.enabled);
  const [time, setTime] = useState(initial.time);
  const [raw, setRaw] = useState(initial.recipients.join('\n'));
  const [timeErr, setTimeErr] = useState<string | null>(null);
  const [recErr, setRecErr] = useState<string | null>(null);
  const [serverErr, setServerErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const save = async () => {
    setOk(false);
    setServerErr(null);
    const recipients = parseRecipients(raw);
    const tErr = TIME_RE.test(time) ? null : t('dailyMail.errTime');
    let rErr: string | null = null;
    if (recipients.length > MAX_RECIPIENTS) {
      rErr = t('dailyMail.errMax', { max: MAX_RECIPIENTS });
    } else {
      const bad = recipients.find((r) => !emailSchema.safeParse(r).success);
      if (bad) rErr = t('dailyMail.errEmail', { value: bad });
    }
    setTimeErr(tErr);
    setRecErr(rErr);
    if (tErr || rErr) return;
    try {
      await update.mutateAsync({
        id: warehouseId,
        patch: {
          daily_partials_mail_enabled: enabled,
          daily_partials_mail_time: time,
          daily_partials_mail_recipients: recipients,
        },
      });
      setOk(true);
    } catch (e: unknown) {
      const err = e as { body?: { detail?: string } };
      setServerErr(err?.body?.detail ?? t('dailyMail.errGeneric'));
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <p className="text-muted small mb-0">{t('dailyMail.intro')}</p>

      <FormGroup switch className="mb-0">
        <Input
          type="switch" role="switch" id={`dm-enabled-${warehouseId}`}
          checked={enabled} disabled={!canEdit}
          onChange={(e) => { setEnabled(e.target.checked); setOk(false); }}
        />
        <Label check for={`dm-enabled-${warehouseId}`}>{t('dailyMail.enabled')}</Label>
      </FormGroup>

      <FormGroup className="mb-0" style={{ maxWidth: 200 }}>
        <Label for={`dm-time-${warehouseId}`}>{t('dailyMail.time')}</Label>
        <Input
          type="time" id={`dm-time-${warehouseId}`} value={time}
          disabled={!canEdit} invalid={timeErr !== null}
          onChange={(e) => { setTime(e.target.value); setOk(false); }}
        />
        {timeErr && <div className="invalid-feedback d-block">{timeErr}</div>}
      </FormGroup>

      <FormGroup className="mb-0">
        <Label for={`dm-rec-${warehouseId}`}>{t('dailyMail.recipients')}</Label>
        <Input
          type="textarea" rows={4} id={`dm-rec-${warehouseId}`} value={raw}
          disabled={!canEdit} invalid={recErr !== null}
          onChange={(e) => { setRaw(e.target.value); setOk(false); }}
        />
        {recErr && <div className="invalid-feedback d-block">{recErr}</div>}
        <FormText className="text-muted">{t('dailyMail.recipientsHint')}</FormText>
      </FormGroup>

      <span className="text-muted small">
        <i className="bi bi-info-circle me-1" />
        {initial.lastClosedOn
          ? t('dailyMail.lastClosed', { date: formatDay(initial.lastClosedOn) })
          : t('dailyMail.never')}
      </span>

      {serverErr && <Alert color="danger" className="mb-0 py-2" role="alert">{serverErr}</Alert>}
      {ok && <Alert color="success" className="mb-0 py-2">{t('dailyMail.saved')}</Alert>}

      {canEdit && (
        <div>
          <Button color="primary" disabled={update.isPending} onClick={() => void save()}>
            {t('dailyMail.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
